import React, { useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import useInterval from "react-useinterval";
// @ts-ignore -- untyped module
import { useMediaQuery } from "react-responsive/src";

import { fixtureTime } from "../common/DateTimeHelper";
import { getRoundName } from "../common/RoundHelper";
import { Fixture } from "../models/fixture";
import { DetailedRound } from "../models/round";
import useOutcomeTree from "../rounds/useOutcomeTree";
import useDetailedFixture from "../fixtures/useDetailedFixture";
import { gradientsC, GetFlexNumber, IsRightToLeftTheme, themeC } from "../theme/themehelper";
import { DbetEightIcon, DbetEightLadder, DbetSixIcon } from "../images/dbeticons";
import { Unibet1x2TileLogo } from "../images/unibeticons";
import { getSurvivorThemeColors, getSurvivorThemeFlags } from "../couponview/survivortheme";
import { getTeamName } from "../slimcoupon/couponhelpers";
import WinInfoRound from "./components/wininforound";
import ScoreBoard from "./components/scoreboard";
import MatchEvents from "./components/selectedfixture/matchevents";
import unibet_football from "../images/unibet_football.jpg";
import hajper8 from "../images/hajper_8.png";
import snabbareSurvivorBg from "../images/snabbare_survivor_bg.png";
import hardSurvivor from "../images/hard_survivor_65.png";
import stuganBackground from "../images/stugan_background.png";
import woodRocket from "../images/wood_rocket.png";
import orangeRocket from "../images/orange_rocket.png";
import lionRocket from "../images/lion_rocket.png";

interface SurvivorRoundProps {
    round: DetailedRound;
}

const SurvivorRound: React.FC<SurvivorRoundProps> = ({ round }) => {
    const { t } = useTranslation();

    const themeFlags = getSurvivorThemeFlags((window as any).theme);
    const { isGreen, isKryss, isLyllo, isDbet, isOrange, isOrangeLight, isWood, isLion, isHajper, isStugan, isSnabbare, isHard } = themeFlags;
    const tc = getSurvivorThemeColors(themeFlags);

    const roundId = round.id;

    const correctSigns = (round?.fixtures ?? []).map((fixture, idx) => {
        if (fixture.status !== "FINISHED") return undefined;
        const sign = round?.preliminaryCorrectRow?.[idx];
        return sign === "ONE" ? "1" : sign === "TWO" ? "2" : "X";
    });

    const anyGameStarted = (round?.fixtures ?? []).some(f => f.status === "PLAYING" || f.status === "FINISHED");
    const outcomeTreeResponse = useOutcomeTree(anyGameStarted ? roundId : undefined, correctSigns, Number(round?.operatorStakePerRow?.amount) || 1);
    const numberOfCouponsLeftPerOutcome = outcomeTreeResponse.outcomeTree;

    useInterval(() => {
        outcomeTreeResponse?.reloadOutcomeTree?.();
    }, 20000);

    const numRows = Math.round(round.operatorTurnover.amount / round.operatorStakePerRow.amount);

    const locale = t('locale') || 'sv-SE';
    const formatCount = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);

    const gameNumbers = round.fixtures.map((_, idx) => idx + 1);

    const firstUnfinishedGame = gameNumbers.find((gameNum) => {
        const fixture = round.fixtures[gameNum - 1];
        return fixture && fixture.status !== "FINISHED";
    });

    const calculateRowsLeftPerGame = () => {
        const result: { [gameNumber: number]: { count: number; percent: number } } = {};

        gameNumbers.forEach((gameNumber) => {
            const totalGameData = numberOfCouponsLeftPerOutcome?.[gameNumber];
            if (!totalGameData) return;

            const fixture = round.fixtures[gameNumber - 1];
            if (!fixture) return;

            const isFinished = fixture.status === "FINISHED";
            const isPlaying = fixture.status === "PLAYING";
            const correctSignRaw = round.preliminaryCorrectRow?.[gameNumber - 1];
            const correctKey =
                correctSignRaw === "ONE" ? "1" : correctSignRaw === "TWO" ? "2" : correctSignRaw === "X" ? "X" : undefined;

            let rowsLeft: number;
            if (isFinished && correctKey) {
                rowsLeft = totalGameData[correctKey as keyof typeof totalGameData] ?? 0;
            } else if (isPlaying) {
                const homeScore = fixture.homeTeam.score ?? 0;
                const awayScore = fixture.awayTeam.score ?? 0;
                const currentKey = homeScore > awayScore ? "1" : awayScore > homeScore ? "2" : "X";
                rowsLeft = totalGameData[currentKey as keyof typeof totalGameData] ?? 0;
            } else {
                rowsLeft = Object.values(totalGameData).reduce((sum, count) => sum + count, 0);
            }

            const percent = numRows > 0 ? (rowsLeft / numRows) * 100 : 0;
            result[gameNumber] = { count: rowsLeft, percent };
        });

        return result;
    };

    const rowsLeftPerGame = calculateRowsLeftPerGame();

    const isAfterDeadline = new Date(round.openTo) < new Date();

    const isSmallScreen = useMediaQuery({ query: "(max-width: 950px)" });
    const liveGameIndex = round.fixtures.findIndex(f => f.status === "PLAYING");
    const lastFinishedGameIndex = round.fixtures.map(f => f.status).lastIndexOf("FINISHED");
    const defaultSelectedIndex = liveGameIndex !== -1
        ? liveGameIndex
        : lastFinishedGameIndex !== -1
            ? lastFinishedGameIndex
            : 0;
    const [selectedFixtureIndex, setSelectedFixtureIndex] = useState<number>(
        isSmallScreen ? -1 : defaultSelectedIndex
    );

    const MatchInfoPanel: React.FC<{ fixture: Fixture; hideScoreBoard?: boolean }> = ({ fixture, hideScoreBoard }) => {
        const { fixtureDetails } = useDetailedFixture(fixture.id);
        const isRightToLeft = IsRightToLeftTheme((window as any).theme);
        return (
            <Box sx={{ backgroundColor: "var(--background_1)", color: "var(--text-black-dark-background)", width: '100%' }}>
                {!hideScoreBoard && (
                    <ScoreBoard fixtureDetail={fixtureDetails} roundFixture={fixture} theme={(window as any).theme} round={round} />
                )}
                {fixtureDetails && (
                    <Grid container direction="column" alignItems="center" sx={{ backgroundColor: "var(--fixtureinformation-background)" }}>
                        <Box sx={{ width: '90%', maxWidth: '600px', margin: '0 auto' }}>
                            <MatchEvents fixtureDetails={fixtureDetails} isRightToLeft={isRightToLeft} />
                        </Box>
                    </Grid>
                )}
            </Box>
        );
    };

    const SurvivorFixture: React.FC<{ fixture: Fixture; index: number }> = ({ fixture, index }) => {
        const isFinished = fixture.status === "FINISHED";
        const isPlaying = fixture.status === "PLAYING";
        const correctSign = round.preliminaryCorrectRow?.[index];
        const gameNumber = index + 1;

        const homeScore = fixture.homeTeam.score ?? 0;
        const awayScore = fixture.awayTeam.score ?? 0;
        const currentSign = homeScore > awayScore ? "ONE" : awayScore > homeScore ? "TWO" : "X";

        const totalOutcomeData = numberOfCouponsLeftPerOutcome?.[gameNumber];

        const isNextGame = gameNumber === firstUnfinishedGame;

        const fixtureDate = new Date(fixture.startingAt);
        const today = new Date();
        const isSameDay = fixtureDate.getFullYear() === today.getFullYear()
            && fixtureDate.getMonth() === today.getMonth()
            && fixtureDate.getDate() === today.getDate();
        const isUpcoming = !isFinished && !isPlaying;

        const getStatusText = () => {
            if (isFinished) return t('full_time');
            if (isPlaying) return 'LIVE';
            if (isUpcoming && !isSameDay) {
                const weekday = fixtureDate.toLocaleDateString(locale, { weekday: 'long' }).replace(/^./, c => c.toUpperCase());
                const time = fixtureDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
                return `${weekday} ${time}`;
            }
            return fixtureTime(fixture, t);
        };
        const statusText = getStatusText();

        const renderSignBox = (label: string) => {
            const isCorrect = isFinished && correctSign === label;
            const isLiveCurrent = isPlaying && currentSign === label;
            const displaySign = label === "ONE" ? "1" : label === "TWO" ? "2" : "X";

            let bg = tc.signBg;
            let border = tc.signBorder;
            let color: string = tc.signColor;

            if (isCorrect) {
                border = tc.signCorrectBorder;
            }
            if (isLiveCurrent) {
                border = tc.liveBorder;
            }

            // Lion: finished games use a greyed-out style for non-correct signs
            if (isLion && isFinished) {
                bg = '#CBCBCB';
                color = '#A8A8A8';
                border = 'none';
                if (isCorrect) {
                    border = tc.signCorrectBorder;
                }
            }

            return (
                <Grid
                    sx={{
                        position: 'relative',
                        width: 48,
                        minWidth: 48,
                        maxWidth: 48,
                        height: 48,
                        minHeight: 48,
                        maxHeight: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        border,
                        backgroundColor: bg,
                        color,
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: "20px",
                        fontFamily: tc.fontFamily,
                        boxSizing: 'border-box',
                    }}
                >
                    {displaySign}
                </Grid>
            );
        };

        const renderRowCount = (label: string, data: { "1": number; "X": number; "2": number } | undefined, labelColor: string, valueColor: string) => {
            if (!data) return null;
            return (
                <Grid container alignItems="center" sx={{ paddingTop: "4px", paddingRight: isSmallScreen ? "32px" : 0 }}>
                    <Grid item sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: labelColor }}>
                            {label}
                        </Typography>
                    </Grid>
                    <Grid item sx={{ width: 152, display: 'flex' }}>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: valueColor, textAlign: "center", flex: 1 }}>
                            {formatCount(data["1"])}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: valueColor, textAlign: "center", flex: 1 }}>
                            {formatCount(data["X"])}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: valueColor, textAlign: "center", flex: 1 }}>
                            {formatCount(data["2"])}
                        </Typography>
                    </Grid>
                </Grid>
            );
        };

        const isSelected = selectedFixtureIndex === index;

        return (
            <Grid sx={{ borderBottom: tc.fixtureBorder, background: (!isSmallScreen && isSelected) ? 'rgba(0, 0, 0, 0.04)' : undefined }}>
                <Grid
                    onClick={isSmallScreen ? undefined : () => setSelectedFixtureIndex(index)}
                    sx={{ padding: "8px 12px 0px", cursor: isSmallScreen ? 'default' : 'pointer' }}
                >
                <Grid container sx={{ paddingBottom: "8px", gap: "8px", flexWrap: "nowrap" }}>
                    {/* Game number */}
                    <Grid item sx={{ width: 12, flexShrink: 0, display: "flex", alignItems: "flex-start" }}>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: tc.textPrimary, textAlign: "center", fontWeight: 400 }}>
                            {gameNumber}
                        </Typography>
                    </Grid>

                    {/* Content */}
                    <Grid item sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                        {/* Status row */}
                        <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ flexWrap: "nowrap" }}>
                            <Grid item sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: isPlaying ? "#F74640" : tc.textSecondary, fontWeight: 400 }}>
                                    {statusText}
                                </Typography>
                                {isPlaying && (
                                    <Typography sx={{ fontSize: "10px", lineHeight: "14px", color: tc.textSecondary, fontWeight: 400 }}>
                                        {t('started')} {fixtureDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                )}
                            </Grid>
                            {rowsLeftPerGame[gameNumber] && anyGameStarted && (
                                <Grid item sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                    <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: tc.textSecondary, fontWeight: 400, whiteSpace: "nowrap" }}>
                                        {t('number_of_rows_left')}: {formatCount(rowsLeftPerGame[gameNumber].count)} ({rowsLeftPerGame[gameNumber].percent.toFixed(1)}%)
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>

                        {/* Teams + Sign boxes */}
                        <Grid container alignItems="center" sx={{ gap: "8px", flexWrap: "nowrap" }}>
                            {/* Team names + scores */}
                            <Grid item sx={{ flex: 1, minWidth: 0 }}>
                                <Grid container justifyContent="space-between" alignItems="center" sx={{ height: 20, flexWrap: "nowrap" }}>
                                    <Typography noWrap sx={{ fontSize: "14px", lineHeight: "20px", color: tc.textPrimary, fontWeight: 400 }}>
                                        {getTeamName(fixture.homeTeam, t)}
                                    </Typography>
                                    {(isFinished || isPlaying) && (
                                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: isPlaying ? "#ff9800" : tc.textPrimary, fontWeight: 400, minWidth: 20, textAlign: "center" }}>
                                            {homeScore}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid container justifyContent="space-between" alignItems="center" sx={{ height: 20, flexWrap: "nowrap" }}>
                                    <Typography noWrap sx={{ fontSize: "14px", lineHeight: "20px", color: tc.textPrimary, fontWeight: 400 }}>
                                        {getTeamName(fixture.awayTeam, t)}
                                    </Typography>
                                    {(isFinished || isPlaying) && (
                                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: isPlaying ? "#ff9800" : tc.textPrimary, fontWeight: 400, minWidth: 20, textAlign: "center" }}>
                                            {awayScore}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>

                            {/* Sign boxes */}
                            <Grid item sx={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                                {renderSignBox("ONE")}
                                {renderSignBox("X")}
                                {renderSignBox("TWO")}
                            </Grid>

                            {/* Expand / collapse arrow (mobile only) */}
                            {isSmallScreen && (
                                <Grid
                                    item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFixtureIndex(isSelected ? -1 : index);
                                    }}
                                    sx={{ display: "flex", alignItems: "center", flexShrink: 0, cursor: "pointer" }}
                                >
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            fontSize: 24,
                                            color: tc.textSecondary,
                                            transition: "transform 0.2s",
                                            transform: isSelected ? "rotate(180deg)" : "rotate(0deg)",
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        {/* Row counts */}
                        {isNextGame && isAfterDeadline && (
                            renderRowCount(t('number_of_rows_left'), totalOutcomeData, tc.textSecondary, tc.textSecondary)
                        )}
                    </Grid>
                </Grid>
                </Grid>
                {isSmallScreen && isSelected && (
                    <Box sx={{ paddingBottom: '8px' }}>
                        <MatchInfoPanel fixture={fixture} hideScoreBoard />
                    </Box>
                )}
            </Grid>
        );
    };

    const numRowsLeft = (() => {
        if (firstUnfinishedGame && numberOfCouponsLeftPerOutcome?.[firstUnfinishedGame]) {
            const data = numberOfCouponsLeftPerOutcome[firstUnfinishedGame];
            return Object.values(data).reduce((sum, count) => sum + count, 0);
        }
        const lastGameNumber = gameNumbers.length > 0 ? Math.max(...gameNumbers) : 0;
        const lastGameData = numberOfCouponsLeftPerOutcome?.[lastGameNumber];
        return lastGameData ? Object.values(lastGameData).reduce((sum, count) => sum + count, 0) : 0;
    })();

    const roundName = getRoundName(round, t);
    const ribbonGradient = (window as any).theme === themeC.Wood
        ? gradientsC.Wood.Flex
        : isDbet
            ? gradientsC.Dbet.Flex
            : isLyllo
                ? gradientsC.Lyllo.Flex
                : isKryss
                    ? gradientsC.Kryss.Flex
                    : isGreen
                        ? gradientsC.Green.Flex
                        : isLion
                            ? gradientsC.Lion.Flex
                            : isOrange
                                ? gradientsC.Orange.Flex
                                : isHajper
                                    ? gradientsC.Hajper.Flex
                                    : isStugan
                                        ? gradientsC.Stugan.Flex
                                        : isSnabbare
                                        ? gradientsC.Snabbare.Flex
                                        : gradientsC.GoGo.Flex;

    // Countdown
    const SurvivorCountdown: React.FC = () => {
        const targetTime = new Date(round.openTo).getTime();
        const cutoff = new Date(Date.now());
        cutoff.setHours(cutoff.getHours() + 5);
        const showCountDown = new Date(round.openTo) < cutoff && !isAfterDeadline;

        const [timeLeft, setTimeLeft] = useState(targetTime - Date.now());

        useEffect(() => {
            if (!showCountDown) return;
            const id = setInterval(() => setTimeLeft(targetTime - Date.now()), 1000);
            return () => clearInterval(id);
        }, [targetTime, showCountDown]);

        if (!showCountDown) {
            return (
                <Typography sx={{ fontFamily: tc.fontFamily, fontWeight: isOrange ? 500 : 400, fontSize: '10px', lineHeight: '15px', color: isOrange ? '#FFFFFF' : tc.textOnRibbon }}>
                    {isAfterDeadline ? t('started') : t('starts')} {new Date(round.openTo).toLocaleDateString()}
                </Typography>
            );
        }

        const total = Math.max(0, timeLeft);
        const days = Math.floor(total / (1000 * 60 * 60 * 24));
        const hours = Math.floor(total / (1000 * 60 * 60)) % 24;
        const minutes = Math.floor(total / (1000 * 60)) % 60;
        const seconds = Math.floor(total / 1000) % 60;

        const pillStyle = {
            background: tc.countdownPillBg,
            borderRadius: '9px',
            padding: '3px 6px',
            display: 'inline-flex',
            alignItems: 'center',
        };
        const pillTextColor = (isLion || isOrange) ? '#1F1F1F' : tc.textOnRibbon;
        const labelTextColor = (isOrange || isLion) ? '#FFFFFF' : pillTextColor;
        const textStyle: React.CSSProperties = { fontFamily: tc.fontFamily, fontWeight: (isOrange || isLion || isWood || isHajper) ? 700 : 400, fontSize: '10px', lineHeight: '10px', color: pillTextColor };
        const labelStyle: React.CSSProperties = { fontFamily: tc.fontFamily, fontWeight: 500, fontSize: '10px', lineHeight: '10px', color: labelTextColor };
        const sepStyle: React.CSSProperties = { ...textStyle, color: labelTextColor, padding: '0 1px', fontWeight: 700 };
        const fmt = (n: number) => n.toString().padStart(2, '0');

        return (
            <Grid container alignItems="center" sx={{ gap: "2px", justifyContent: "center" }}>
                {(isOrange || isLion) && (
                    <span style={{ ...labelStyle, padding: '3px', marginRight: '2px' }}>{t('starts')}</span>
                )}
                {days > 0 && (
                    <>
                        <Box sx={pillStyle}><span style={textStyle}>{fmt(days)}d</span></Box>
                        <span style={sepStyle}>,</span>
                    </>
                )}
                <Box sx={pillStyle}><span style={textStyle}>{fmt(hours)}h</span></Box>
                <span style={sepStyle}>:</span>
                <Box sx={pillStyle}><span style={textStyle}>{fmt(minutes)}m</span></Box>
                <span style={sepStyle}>:</span>
                <Box sx={pillStyle}><span style={textStyle}>{fmt(seconds)}s</span></Box>
            </Grid>
        );
    };

    const selectedFixture = (!isSmallScreen && selectedFixtureIndex >= 0) ? round.fixtures[selectedFixtureIndex] : undefined;

    return (
        <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', justifyContent: 'center', alignItems: 'flex-start', width: '100%', background: tc.pageBg, gap: isSmallScreen ? 0 : '24px' }}>
        <Grid container direction="column" alignItems="center" sx={{ width: isSmallScreen ? '100%' : 355, flexShrink: 0 }}>
          <Grid container direction="column" alignItems="center" sx={{ background: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? '#FFFFFF' : 'transparent', width: '100%', maxWidth: (isGreen || isKryss || isLyllo || isDbet || isOrange || isLion || isWood || isHajper || isStugan || isSnabbare || isHard) ? 355 : undefined, borderRadius: (isKryss || isLyllo) ? '12px' : (isDbet || isStugan || isSnabbare) ? '10px' : (isOrange || isLion || isWood || isHajper || isHard) ? '8px' : undefined, boxShadow: (isOrange || isLion || isWood || isHajper || isHard) ? '0px 1px 13px -2px rgba(16, 24, 40, 0.08)' : undefined }}>
            <Grid container direction="column" alignItems="center" sx={{ background: isWood ? '#1E1E1E' : 'transparent', width: '100%', borderRadius: isWood ? '8px' : undefined, overflow: isWood ? 'hidden' : undefined }}>
            {/* Ribbon tag */}
            <Grid sx={{
                width: '100%',
                maxWidth: 355,
                backgroundImage: isGreen ? `url(${unibet_football})` : isHajper ? `url(${hajper8})` : isWood ? `url(${woodRocket})` : isOrange ? `url(${orangeRocket})` : isLion ? `url(${lionRocket})` : isStugan ? `url(${stuganBackground})` : isSnabbare ? `url(${snabbareSurvivorBg})` : isHard ? `url(${hardSurvivor})` : ribbonGradient,
                backgroundSize: (isWood || isOrange || isLion) ? '100% auto' : 'cover',
                backgroundRepeat: (isWood || isOrange || isLion) ? 'no-repeat' : undefined,
                backgroundPosition: isGreen ? 'center 64%' : (isHajper || isHard) ? 'center' : (isWood || isOrange || isLion) ? 'right bottom' : undefined,
                borderRadius: (isKryss || isLyllo) ? '12px 12px 0px 0px' : (isDbet || isStugan || isSnabbare) ? '10px 10px 0px 0px' : '8px 8px 0px 0px',
                padding: isGreen ? '0px' : '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                minHeight: isGreen ? '100px' : isHard ? '104px' : undefined,
            }}>
                {isGreen && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(180deg, rgba(14, 60, 22, 0.5) 30%, rgba(62, 171, 55, 0.5) 100%)',
                        borderRadius: '8px 8px 0px 0px',
                    }} />
                )}
                {isGreen && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                        <Unibet1x2TileLogo width={32} height={19} />
                    </Box>
                )}
                <Box sx={{
                    position: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'relative' : undefined,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? '4px' : '8px',
                    width: '100%',
                    height: isGreen ? '100px' : undefined,
                    padding: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? '12px' : undefined,
                    boxSizing: 'border-box',
                }}>
                    {isDbet ? (
                        <>
                            <Typography sx={{
                                fontFamily: 'Righteous',
                                fontWeight: 400,
                                fontSize: '20px',
                                lineHeight: '24px',
                                color: '#F4C004',
                                textTransform: 'uppercase',
                            }}>
                                {t('survivor8')}
                            </Typography>
                            <Grid container direction="row" alignItems="center" justifyContent="center">
                                <Grid sx={{ marginRight: "10px" }}>
                                    {GetFlexNumber(round.name) === "6" ? DbetSixIcon() : DbetEightIcon()}
                                </Grid>
                                {DbetEightLadder()}
                            </Grid>
                        </>
                    ) : isSnabbare ? (
                        <>
                            {/* Snabbare survivor lockup — keep in sync with survivorcoupon.tsx. */}
                            <Typography sx={{
                                fontFamily: 'var(--font-title)',
                                fontWeight: 'var(--font-title-weight)',
                                fontSize: '52px',
                                lineHeight: '52px',
                                letterSpacing: '-0.01em',
                                color: tc.textOnRibbon,
                                textTransform: 'uppercase',
                                fontStyle: 'normal'
                            }}>
                                {GetFlexNumber(round.name)}
                            </Typography>
                            <Typography sx={{
                                fontFamily: 'var(--font-body)',
                                fontWeight: 700,
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '0.34em',   // wide tracking to match the brand wordmark
                                textIndent: '0.34em',       // balance the trailing space so it stays centered
                                color: tc.textOnRibbon,
                                textTransform: 'uppercase',
                                fontStyle: 'normal'
                            }}>
                                {t('survivor8')}
                            </Typography>
                        </>
                    ) : isStugan ? (
                        <>
                            {/* Stugan survivor lockup — keep in sync with survivorcoupon.tsx. */}
                            <Typography sx={{
                                fontFamily: 'var(--font-title)',
                                fontWeight: 'var(--font-title-weight)',
                                fontSize: '40px',
                                lineHeight: '40px',
                                color: tc.textOnRibbon,
                            }}>
                                {GetFlexNumber(round.name)}
                            </Typography>
                            <Typography sx={{
                                fontFamily: 'var(--font-body)',
                                fontWeight: 600,
                                fontSize: '16px',
                                lineHeight: '20px',
                                letterSpacing: '0.05em',
                                color: '#FDA89B',
                            }}>
                                {t('survivor8')}
                            </Typography>
                        </>
                    ) : (isKryss || isLyllo) ? (
                        <>
                            <Typography sx={{
                                fontFamily: 'var(--font-title)',
                                fontWeight: 'var(--font-title-weight)',
                                fontSize: '40px',
                                lineHeight: '40px',
                                color: tc.textOnRibbon,
                                textTransform: 'uppercase',
                            }}>
                                {GetFlexNumber(round.name)}
                            </Typography>
                            <Typography sx={{
                                fontFamily: 'var(--font-body)',
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: '20px',
                                color: tc.textOnRibbon,
                                textTransform: 'uppercase',
                            }}>
                                {t('survivor8')}
                            </Typography>
                        </>
                    ) : isGreen ? (
                        <>
                            <Typography sx={{
                                fontFamily: 'UnibetPro',
                                fontWeight: 500,
                                fontSize: '52px',
                                lineHeight: '40px',
                                color: '#FFFFFF',
                            }}>
                                {GetFlexNumber(round.name)}
                            </Typography>
                            <Typography sx={{
                                fontFamily: 'UnibetPro',
                                fontWeight: 500,
                                fontSize: '16px',
                                lineHeight: '20px',
                                color: '#FFFFFF',
                                textTransform: 'uppercase',
                            }}>
                                {t('survivor8')}
                            </Typography>
                        </>
                    ) : isHajper ? (
                        <>
                            <Typography sx={{
                                fontFamily: "'HajperDisplay', sans-serif",
                                fontWeight: 800,
                                fontSize: '52px',
                                lineHeight: '52px',
                                color: '#FFFFFF',
                            }}>
                                {GetFlexNumber(round.name)}
                            </Typography>
                            <Typography sx={{
                                fontFamily: "var(--font-title), sans-serif",
                                fontWeight: 500,
                                fontSize: '16px',
                                lineHeight: '20px',
                                color: '#FFFFFF',
                            }}>
                                {t('survivor8')}
                            </Typography>
                            <SurvivorCountdown />
                        </>
                    ) : (
                        <>
                            {/* Hard: "Tipsraketen" is baked into the plate, so no live round name. */}
                            {!isHard && (
                                <Typography sx={{
                                    fontFamily: tc.fontFamily,
                                    fontWeight: (isOrange || isWood || isHajper) ? 700 : 400,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: isOrange ? '#FFFFFF' : tc.textOnRibbon,
                                    textTransform: 'uppercase',
                                }}>
                                    {roundName}
                                </Typography>
                            )}
                            <SurvivorCountdown />
                        </>
                    )}
                </Box>
            </Grid>

            {/* Scoreboard - Number of rows */}
            <Grid container direction='column' alignItems='center' sx={{
                width: '100%',
                maxWidth: 355,
                background: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--background_4)' : (isLion || isOrangeLight) ? '#F6F6F6' : tc.cardBg,
                padding: '10px',
            }}>
                {(isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) && !isAfterDeadline && (
                    <Grid item>
                        <Grid container direction='column' alignItems='center' sx={{ marginTop: '10px', marginBottom: '0px' }}>
                            <Typography sx={{ fontSize: '12px', color: 'var(--font-color-supportive)', lineHeight: '18px', fontWeight: 400, fontFamily: 'var(--font-body)' }}>
                                {t('starts')}
                            </Typography>
                            <Typography sx={{ fontSize: '16px', color: 'var(--text-black)', lineHeight: '24px', fontWeight: 700, fontFamily: 'var(--font-body)' }}>
                                {new Date(round.openTo).toLocaleDateString()}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                {(isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) && isAfterDeadline && (
                    <Grid item>
                        <Grid container direction='column' alignItems='center' sx={{ marginTop: '10px', marginBottom: '0px' }}>
                            <Typography sx={{ fontSize: '12px', color: 'var(--font-color-supportive)', lineHeight: '18px', fontWeight: 400, fontFamily: 'var(--font-body)' }}>
                                {t('status')}
                            </Typography>
                            <Typography sx={{ fontSize: '16px', color: 'var(--text-black)', lineHeight: '24px', fontWeight: 700, fontFamily: 'var(--font-body)' }}>
                                {round.status === 'ACTIVE' ? 'Live' : round.status}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Grid container justifyContent='space-evenly' sx={{ marginTop: '5px' }}>
                    <Grid item>
                        <Grid container direction='column' alignItems='center'>
                            <Typography sx={{ fontSize: '12px', color: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--font-color-supportive)' : tc.textSecondary, lineHeight: '18px', fontWeight: 400, fontFamily: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--font-body)' : tc.fontFamily }}>
                                {t('number_of_rows')}
                            </Typography>
                            <Typography sx={{ fontSize: '16px', color: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--text-black)' : tc.textPrimary, lineHeight: '24px', fontWeight: 700, fontFamily: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--font-body)' : tc.fontFamily }}>
                                {formatCount(numRows)}
                            </Typography>
                        </Grid>
                    </Grid>
                    {isAfterDeadline && (
                        <Grid item>
                            <Grid container direction='column' alignItems='center'>
                                <Typography sx={{ fontSize: '12px', color: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--font-color-supportive)' : tc.textSecondary, lineHeight: '18px', fontWeight: 400, fontFamily: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--font-body)' : tc.fontFamily }}>
                                    {t('number_of_rows_left')}
                                </Typography>
                                <Typography sx={{ fontSize: '16px', color: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--text-black)' : tc.textPrimary, lineHeight: '24px', fontWeight: 700, fontFamily: (isGreen || isKryss || isLyllo || isDbet || isStugan || isSnabbare) ? 'var(--font-body)' : tc.fontFamily }}>
                                    {formatCount(numRowsLeft)}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {/* Games section */}
            <Grid sx={{
                width: '100%',
                maxWidth: 355,
                paddingTop: '12px',
                background: isLion ? '#FFFFFF' : isOrange ? tc.cardBg : undefined,
            }}>
                {round.fixtures.map((fixture, idx) => (
                    <SurvivorFixture key={fixture.id} fixture={fixture} index={idx} />
                ))}
            </Grid>

            {/* Win plan */}
            {isAfterDeadline && (round as any).operatorPrizePlan && (
                <Grid sx={{ width: '100%', maxWidth: 355, margin: '24px auto 8px', alignSelf: 'center' }}>
                    <WinInfoRound round={round as any} isRightToLeft={IsRightToLeftTheme((window as any).theme)} />
                </Grid>
            )}

            <Grid sx={{ height: '24px' }} />
            </Grid>
          </Grid>
        </Grid>
        {selectedFixture && (
            <Box sx={{ width: '100%', maxWidth: 420, marginTop: '16px' }}>
                <MatchInfoPanel fixture={selectedFixture} />
            </Box>
        )}
        </Box>
    );
};

export default SurvivorRound;
